import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { Spinner } from './Spinner';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /** Blocks interaction and announces `aria-busy` while an action is in flight. */
  loading?: boolean | undefined;
  loadingLabel?: string | undefined;
  fullWidth?: boolean | undefined;
  iconStart?: ReactNode | undefined;
  iconEnd?: ReactNode | undefined;
  className?: string | undefined;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingLabel = 'Carregando…',
    fullWidth = false,
    iconStart,
    iconEnd,
    className,
    children,
    disabled = false,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[
        styles.button,
        styles[variant],
        styles[size],
        styles.relative,
        fullWidth ? styles.fullWidth : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        className={[styles.content, loading ? styles.loadingLabel : null].filter(Boolean).join(' ')}
      >
        {iconStart}
        {children}
        {iconEnd}
      </span>
      {loading ? (
        <span className={styles.loadingOverlay}>
          <Spinner size={size === 'lg' ? 'md' : 'sm'} label={loadingLabel} />
        </span>
      ) : null}
    </button>
  );
});
